import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/events";
import { isViewer } from "@/lib/guards";
import {
  INSTALLED_KEY, PUSH_ASKED_KEY, isStandalone, isIos, isBannerDismissed,
  dismissBanner, shouldShowBanner, urlBase64ToUint8Array,
} from "@/lib/pwa";

export default function usePwa({ user, onAppSurface }) {
  const nav = useNavigate();
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [deferred, setDeferred] = useState(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => isBannerDismissed());
  const [config, setConfig] = useState({ push_enabled: false, vapid_public_key: null });
  const [prefs, setPrefs] = useState({ daily_calendar: true, weekly_pending: true });
  const [subscribed, setSubscribed] = useState(false);

  const allowed = user?.access_status === "allowed";
  const ios = isIos();
  const showBanner = shouldShowBanner({
    standalone, dismissed, allowed, onAppSurface: Boolean(onAppSurface),
  });

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", () => {
      track("pwa_install_accepted");
      setDeferred(null);
    });
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  useEffect(() => {
    if (showBanner) track("pwa_prompt_shown");
  }, [showBanner]);

  useEffect(() => {
    if (!standalone) return;
    try {
      if (!localStorage.getItem(INSTALLED_KEY)) {
        localStorage.setItem(INSTALLED_KEY, "1");
        track("pwa_installed");
      }
    } catch { /* private mode */ }
  }, [standalone]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    api.get("/pwa/config").then((r) => {
      if (!cancelled) setConfig(r.data || {});
    }).catch(() => {});
    if (!isViewer(user)) {
      api.get("/pwa/prefs").then((r) => {
        if (!cancelled) setPrefs(r.data || {});
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [allowed, user]);

  useEffect(() => {
    if (!standalone || !config.push_enabled) return;
    if (isViewer(user)) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    try {
      if (localStorage.getItem(PUSH_ASKED_KEY)) return;
    } catch { return; }
    setPushOpen(true);
    track("pwa_push_prompt_shown");
  }, [standalone, config.push_enabled, user]);

  const onDismiss = useCallback(() => {
    dismissBanner();
    setDismissed(true);
    track("pwa_install_dismissed", { source: "banner" });
  }, []);

  const onInstall = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
      if (choice?.outcome === "accepted") track("pwa_install_accepted");
      else track("pwa_install_dismissed", { source: "native" });
      setDeferred(null);
      return;
    }
    if (ios) {
      setIosOpen(true);
      return;
    }
    nav("/perfil#app-celular");
  }, [deferred, ios, nav]);

  const subscribePush = useCallback(async () => {
    if (!config.vapid_public_key || !("serviceWorker" in navigator)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.vapid_public_key),
    });
    const json = sub.toJSON();
    await api.post("/pwa/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      user_agent: navigator.userAgent,
    });
    setSubscribed(true);
    return true;
  }, [config.vapid_public_key]);

  const onAllowPush = useCallback(async () => {
    try { localStorage.setItem(PUSH_ASKED_KEY, "1"); } catch { /* */ }
    setPushOpen(false);
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      track("pwa_push_granted");
      await subscribePush().catch(() => {});
    } else {
      track("pwa_push_denied");
    }
  }, [subscribePush]);

  const onLaterPush = useCallback(() => {
    try { localStorage.setItem(PUSH_ASKED_KEY, "1"); } catch { /* */ }
    setPushOpen(false);
    track("pwa_push_denied", { source: "later" });
  }, []);

  const sendTestPush = useCallback(async () => {
    const { data } = await api.post("/pwa/test-push");
    return data;
  }, []);

  const patchPrefs = useCallback(async (patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch };
      api.patch("/pwa/prefs", patch).then((r) => {
        if (r.data) setPrefs(r.data);
      }).catch(() => setPrefs(p));
      return next;
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "push") return;
    track("pwa_push_clicked");
    params.delete("from");
    const q = params.toString();
    const next = `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", next);
  }, []);

  useEffect(() => {
    setStandalone(isStandalone());
  }, []);

  return {
    standalone,
    ios,
    showBanner,
    deferred,
    iosOpen,
    setIosOpen,
    pushOpen,
    config,
    prefs,
    subscribed,
    onInstall,
    onDismiss,
    onAllowPush,
    onLaterPush,
    patchPrefs,
    sendTestPush,
  };
}
